import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, Button, IconButton,
  Tab, Tabs, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, TextField, CircularProgress, Snackbar, Alert, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, InputAdornment, Tooltip, LinearProgress,
  Switch, FormControlLabel, Divider, Select, MenuItem, FormControl,
  InputLabel, Paper,
} from '@mui/material';
import {
  Add, Edit, Search, Refresh, EvStation, CheckCircle,
  Cancel, ViewList, Map as MapIcon, LocationOn, Close,
  BatteryChargingFull, BatteryAlert, Analytics, Notifications,
  BatteryFull, Battery20, Battery50, Battery80, ElectricCar,
  Timeline, Warning, Check, Clear,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const greenIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});
const redIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});
const orangeIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png',
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const EMPTY_FORM = {
  name: '', address: '', latitude: '', longitude: '',
  total_slots: '', available_slots: '', is_active: 1,
  charging_rate_kw: '22.0', 
  pricing_per_kwh: '', 
  connector_types: [],
  availability_status: 'available',
};

// slot fill % → colour
const slotColor = (avail, total) => {
  const pct = total > 0 ? ((total - avail) / total) * 100 : 0;
  if (pct >= 90) return '#dc2626';
  if (pct >= 60) return '#d97706';
  return '#16a34a';
};

const mapIcon = (avail, total, active) => {
  if (!active) return redIcon;
  const pct = total > 0 ? ((total - avail) / total) * 100 : 0;
  if (pct >= 90) return redIcon;
  if (pct >= 60) return orangeIcon;
  return greenIcon;
};

// ── Stat card ─────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, sub, color }) => (
  <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 2.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {React.cloneElement(icon, { sx: { color, fontSize: 26 } })}
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={800} color="#1a237e" sx={{ lineHeight: 1.1 }}>{value}</Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={500}>{label}</Typography>
        {sub && <Typography variant="caption" color={color} fontWeight={700} display="block">{sub}</Typography>}
      </Box>
    </Box>
  </Card>
);

export default function BatteryStations() {
  const [rows, setRows] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [tab, setTab] = useState(0); // 0 = Stations, 1 = Map, 2 = Battery Monitor
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editStation, setEditStation] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });

  // Battery monitoring states
  const [batteryData, setBatteryData] = useState({ vehicles: [], stats: {} });
  const [alerts, setAlerts] = useState([]);
  const [chargingSessions, setChargingSessions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  // Dialogs
  const [analyticsDialog, setAnalyticsDialog] = useState(false);
  const [chargingDialog, setChargingDialog] = useState(false);

  // Inline editing state
  const [editingSlots, setEditingSlots] = useState(null); // station ID being edited
  const [tempSlots, setTempSlots] = useState(''); // temporary input value

  // Battery monitoring functions
  const loadBatteryData = async () => {
    try {
      const res = await InvokeApi(Config.base_url + '/admin/battery/status', {});
      if (res?.code === '200') {
        setBatteryData(res);
      }
    } catch (err) {
      console.error('Failed to load battery data:', err);
    }
  };

  const loadAlerts = async () => {
    try {
      const res = await InvokeApi(Config.base_url + '/admin/battery/alerts', { limit: 50 });
      if (res?.code === '200') {
        setAlerts(res.alerts || []);
      }
    } catch (err) {
      console.error('Failed to load alerts:', err);
    }
  };

  const loadChargingSessions = async () => {
    try {
      const res = await InvokeApi(Config.base_url + '/admin/charging/sessions', { limit: 50 });
      if (res?.code === '200') {
        setChargingSessions(res.sessions || []);
      }
    } catch (err) {
      console.error('Failed to load charging sessions:', err);
    }
  };

  const loadAnalytics = async (vehicleId, days = 7) => {
    try {
      const res = await InvokeApi(Config.base_url + '/admin/battery/analytics', {
        vehicle_id: vehicleId,
        days: days
      });
      if (res?.code === '200') {
        setAnalytics(res);
        setAnalyticsDialog(true);
      }
    } catch (err) {
      console.error('Failed to load analytics:', err);
    }
  };

  const checkBatteryAlerts = async () => {
    try {
      const res = await InvokeApi(Config.base_url + '/admin/battery/check-alerts', {});
      if (res?.code === '200') {
        setSnack({ open: true, msg: `Battery check complete! ${res.alerts_created} new alerts created from ${res.vehicles_checked} vehicles checked.`, sev: 'success' });
        loadAlerts(); // Refresh alerts
      }
    } catch (err) {
      console.error('Failed to check alerts:', err);
      setSnack({ open: true, msg: 'Failed to check battery alerts', sev: 'error' });
    }
  };

  // Battery utility functions
  const getBatteryColor = (percentage) => {
    if (percentage >= 60) return '#16A34A'; // Green - Good
    if (percentage >= 30) return '#D97706'; // Orange - Medium  
    return '#DC2626';                       // Red - Low
  };

  const getBatteryIcon = (percentage, isCharging = false) => {
    if (isCharging) return <BatteryChargingFull sx={{ color: '#059669' }} />;
    if (percentage >= 80) return <BatteryFull sx={{ color: '#16A34A' }} />;
    if (percentage >= 50) return <Battery80 sx={{ color: '#16A34A' }} />;
    if (percentage >= 20) return <Battery50 sx={{ color: '#D97706' }} />;
    return <Battery20 sx={{ color: '#DC2626' }} />;
  };

  // Auto-refresh battery data every 30 seconds when on battery monitoring tab
  useEffect(() => {
    if (tab === 2 && autoRefresh) {
      loadBatteryData();
      loadAlerts();
      loadChargingSessions();

      const interval = setInterval(() => {
        loadBatteryData();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [tab, autoRefresh]);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchStations = useCallback(async () => {
    setLoading(true);
    // Use real-time status endpoint instead of basic getStations
    const res = await InvokeApi(Config.base_url + '/admin/stations/realtime-status', {});
    if (res?.code === '200') {
      setRows((res.stations ?? []).map((r, i) => ({ ...r, id: r.id ?? i + 1 })));
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStations(); }, [fetchStations]);

  // Auto-refresh stations data every 30 seconds for real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      fetchStations();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [fetchStations]);

  // ── Local filter ──────────────────────────────────────────────────────────
  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      rows.filter((r) => {
        const matchSearch =
          !q ||
          r.name?.toLowerCase().includes(q) ||
          r.address?.toLowerCase().includes(q);
        const matchStatus =
          statusFilter === '' ? true :
            statusFilter === 'active' ? !!r.is_active :
              statusFilter === 'inactive' ? !r.is_active :
                statusFilter === 'full' ? (r.available_slots ?? 0) === 0 :
                  statusFilter === 'available' ? (r.available_slots ?? 0) > 0 : true;
        return matchSearch && matchStatus;
      })
    );
    setPage(0);
  }, [search, statusFilter, rows]);

  // ── Stats ─────────────────────────────────────────────────────────────────
  const totalSlots = rows.reduce((s, r) => s + (parseInt(r.total_slots) || 0), 0);
  const availableSlots = rows.reduce((s, r) => s + (parseInt(r.available_slots) || 0), 0);
  const activeCount = rows.filter(r => r.is_active).length;
  const fullCount = rows.filter(r => (r.available_slots ?? 0) === 0 && r.is_active).length;

  const paginated = filtered.slice(page * pageSize, page * pageSize + pageSize);
  const validStations = filtered.filter(r => r.latitude && r.longitude);

  // ── Dialog helpers ────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditStation(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditStation(row);
    setForm({
      name: row.name ?? '',
      address: row.address ?? '',
      latitude: row.latitude ?? '',
      longitude: row.longitude ?? '',
      total_slots: row.total_slots ?? '',
      available_slots: row.available_slots ?? '',
      is_active: row.is_active ?? 1,
      charging_rate_kw: row.charging_rate_kw ?? '22.0',
      pricing_per_kwh: row.pricing_per_kwh ?? '',
      connector_types: row.connector_types ? (Array.isArray(row.connector_types) ? row.connector_types : JSON.parse(row.connector_types || '[]')) : [],
      availability_status: row.availability_status ?? 'available',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setSnack({ open: true, msg: 'Station name is required.', sev: 'error' }); return; }
    if (!form.address.trim()) { setSnack({ open: true, msg: 'Address is required.', sev: 'error' }); return; }
    if (!form.total_slots) { setSnack({ open: true, msg: 'Total slots is required.', sev: 'error' }); return; }

    setSaving(true);
    const endpoint = editStation ? apiList.editStation : apiList.addStation;
    const payload = editStation ? { ...form, id: editStation.id } : form;
    const res = await InvokeApi(Config.base_url + endpoint, payload);
    setSaving(false);
    if (res?.code === '200' || res?.status === 'success') {
      setSnack({ open: true, msg: editStation ? 'Station updated.' : 'Station added.', sev: 'success' });
      setDialogOpen(false);
      fetchStations();
    } else {
      setSnack({ open: true, msg: res?.message || 'Save failed.', sev: 'error' });
    }
  };

  // quick inline toggle active
  const handleToggleActive = async (row) => {
    const res = await InvokeApi(Config.base_url + apiList.editStation, {
      id: row.id,
      is_active: row.is_active ? 0 : 1,
    });
    if (res?.code === '200') {
      setSnack({ open: true, msg: 'Status updated.', sev: 'success' });
      fetchStations();
    } else {
      setSnack({ open: true, msg: res?.message || 'Toggle failed.', sev: 'error' });
    }
  };

  // Inline editing for available slots
  const startEditSlots = (station) => {
    setEditingSlots(station.id);
    setTempSlots(station.available_slots?.toString() || '0');
  };

  const cancelEditSlots = () => {
    setEditingSlots(null);
    setTempSlots('');
  };

  const saveSlots = async (station) => {
    const newSlots = parseInt(tempSlots) || 0;
    const totalSlots = parseInt(station.total_slots) || 0;

    // Validation
    if (newSlots < 0) {
      setSnack({ open: true, msg: 'Available slots cannot be negative.', sev: 'error' });
      return;
    }
    if (newSlots > totalSlots) {
      setSnack({ open: true, msg: `Available slots cannot exceed total slots (${totalSlots}).`, sev: 'error' });
      return;
    }

    const res = await InvokeApi(Config.base_url + apiList.editStation, {
      id: station.id,
      available_slots: newSlots,
    });

    if (res?.code === '200') {
      setSnack({ open: true, msg: 'Available slots updated.', sev: 'success' });
      setEditingSlots(null);
      setTempSlots('');
      fetchStations();
    } else {
      setSnack({ open: true, msg: res?.message || 'Update failed.', sev: 'error' });
    }
  };

  return (
    <Box>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Battery Stations</Typography>
          <Typography variant="body2" color="text.secondary">
            {filtered.length} station{filtered.length !== 1 ? 's' : ''} found
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchStations} sx={{ color: '#1a237e', border: '1px solid #e8eaf6', borderRadius: 2 }}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openAdd}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, px: 3 }}
          >
            Add Station
          </Button>
        </Box>
      </Box>

      {/* ── Stat cards ──────────────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard icon={<EvStation />} label="Total Stations" value={rows.length} color="#1a237e" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard icon={<CheckCircle />} label="Active Stations" value={activeCount} color="#16a34a"
            sub={rows.length > 0 ? `${Math.round((activeCount / rows.length) * 100)}% operational` : ''} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard icon={<EvStation />} label="Available Slots" value={availableSlots}
            sub={`of ${totalSlots} total`} color="#1565c0" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard icon={<Cancel />} label="Full Stations" value={fullCount} color="#dc2626"
            sub={fullCount > 0 ? 'Needs attention' : 'All good'} />
        </Grid>
      </Grid>

      {/* ── Filters & Tabs ─────────────────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', mb: 3 }}>
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Search */}
            <Grid item xs={12} md={4}>
              <TextField
                size="small"
                placeholder="Search stations..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" sx={{ color: '#64748b' }} />
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#f8fafc',
                    '&:hover': { bgcolor: '#f1f5f9' },
                    '&.Mui-focused': { bgcolor: '#fff' }
                  }
                }}
              />
            </Grid>

            {/* Filter */}
            <Grid item xs={12} md={3}>
              <FormControl size="small" fullWidth>
                <Select
                  value={statusFilter}
                  onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
                  displayEmpty
                  sx={{
                    bgcolor: '#f8fafc',
                    '&:hover': { bgcolor: '#f1f5f9' },
                    '&.Mui-focused': { bgcolor: '#fff' }
                  }}
                >
                  <MenuItem value="">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ViewList fontSize="small" />
                      <Typography variant="body2">All Stations</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="active">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle fontSize="small" sx={{ color: '#16a34a' }} />
                      <Typography variant="body2">Active</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="inactive">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Cancel fontSize="small" sx={{ color: '#dc2626' }} />
                      <Typography variant="body2">Inactive</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="available">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EvStation fontSize="small" sx={{ color: '#1565c0' }} />
                      <Typography variant="body2">Has Slots Available</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="full">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Warning fontSize="small" sx={{ color: '#d97706' }} />
                      <Typography variant="body2">Full (No Slots)</Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Tabs */}
            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button
                  variant={tab === 0 ? 'contained' : 'outlined'}
                  startIcon={<ViewList />}
                  onClick={() => setTab(0)}
                  sx={{
                    borderRadius: 1,
                    px: 1.5,
                    fontWeight: 700,
                    fontSize: 13,
                    textTransform: 'none',
                    ...(tab === 0 ? {
                      background: 'linear-gradient(135deg,#1a237e,#1565c0)',
                      boxShadow: '0 4px 12px rgba(26,35,126,0.3)'
                    } : {
                      borderColor: '#cbd5e1',
                      color: '#64748b',
                      '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' }
                    })
                  }}
                >
                  List View
                </Button>
                <Button
                  variant={tab === 1 ? 'contained' : 'outlined'}
                  startIcon={<MapIcon />}
                  onClick={() => setTab(1)}
                  sx={{
                    borderRadius: 1,
                    px: 1.5,
                    fontWeight: 700,
                    fontSize: 13,
                    textTransform: 'none',
                    ...(tab === 1 ? {
                      background: 'linear-gradient(135deg,#1a237e,#1565c0)',
                      boxShadow: '0 4px 12px rgba(26,35,126,0.3)'
                    } : {
                      borderColor: '#cbd5e1',
                      color: '#64748b',
                      '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' }
                    })
                  }}
                >
                  Map View
                </Button>
                <Button
                  variant={tab === 2 ? 'contained' : 'outlined'}
                  startIcon={<BatteryChargingFull />}
                  onClick={() => setTab(2)}
                  sx={{
                    borderRadius: 1,
                    px: 1.5,
                    fontWeight: 700,
                    fontSize: 13,
                    textTransform: 'none',
                    ...(tab === 2 ? {
                      background: 'linear-gradient(135deg,#059669,#10b981)',
                      boxShadow: '0 4px 12px rgba(5,150,105,0.3)'
                    } : {
                      borderColor: '#cbd5e1',
                      color: '#64748b',
                      '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' }
                    })
                  }}
                >
                  Battery Monitor
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── List View ───────────────────────────────────────────────────────── */}
      {tab === 0 && (
        <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
              <CircularProgress sx={{ color: '#1a237e' }} />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>Station Info</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>Location</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13, textAlign: 'center' }}>Slot Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13, textAlign: 'center' }}>Charging Now</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13, textAlign: 'center' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13, textAlign: 'center' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginated.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                          No stations found
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginated.map((s) => {
                        const total = parseInt(s.total_slots) || 0;
                        const avail = parseInt(s.available_slots) || 0;
                        const used = total - avail;
                        const pct = total > 0 ? Math.round((used / total) * 100) : 0;
                        const color = slotColor(avail, total);
                        return (
                          <TableRow key={s.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                            {/* ID */}
                            <TableCell>
                              <Chip
                                label={`#${s.id}`}
                                size="small"
                                sx={{
                                  bgcolor: '#e8eaf6',
                                  color: '#1a237e',
                                  fontWeight: 700,
                                  fontSize: 11,
                                  height: 24
                                }}
                              />
                            </TableCell>

                            {/* Station Info */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    background: s.is_active
                                      ? 'linear-gradient(135deg,#1a237e,#1565c0)'
                                      : '#cbd5e1',
                                    boxShadow: s.is_active ? '0 2px 8px rgba(26,35,126,0.3)' : 'none'
                                  }}
                                >
                                  <EvStation sx={{ fontSize: 20 }} />
                                </Avatar>
                                <Box>
                                  <Typography fontWeight={700} fontSize={14} color="#1a237e">
                                    {s.name}
                                  </Typography>
                                  <Typography fontSize={11} color="text.secondary">
                                    Total Capacity: {total} slots
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>

                            {/* Location */}
                            <TableCell>
                              <Box>
                                <Typography fontSize={12} color="text.primary" sx={{ mb: 0.5 }}>
                                  {s.address || '—'}
                                </Typography>
                                <Typography fontSize={11} color="text.secondary">
                                  {s.latitude && s.longitude
                                    ? `${parseFloat(s.latitude).toFixed(4)}, ${parseFloat(s.longitude).toFixed(4)}`
                                    : 'No GPS coordinates'}
                                </Typography>
                              </Box>
                            </TableCell>

                            {/* Slot Status */}
                            <TableCell>
                              <Box sx={{ minWidth: 160 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                    {used}/{total} occupied
                                  </Typography>
                                  <Typography variant="caption" fontWeight={700} sx={{ color }}>
                                    {pct}%
                                  </Typography>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={pct}
                                  sx={{
                                    height: 8,
                                    borderRadius: 2,
                                    bgcolor: '#e2e8f0',
                                    '& .MuiLinearProgress-bar': {
                                      bgcolor: color,
                                      borderRadius: 2
                                    }
                                  }}
                                />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                  {editingSlots === s.id ? (
                                    <>
                                      <TextField
                                        size="small"
                                        type="number"
                                        value={tempSlots}
                                        onChange={(e) => setTempSlots(e.target.value)}
                                        sx={{ width: 70 }}
                                        inputProps={{ min: 0, max: s.total_slots }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') saveSlots(s);
                                          if (e.key === 'Escape') cancelEditSlots();
                                        }}
                                        autoFocus
                                      />
                                      <IconButton size="small" onClick={() => saveSlots(s)} sx={{ color: '#16a34a' }}>
                                        <Check fontSize="small" />
                                      </IconButton>
                                      <IconButton size="small" onClick={cancelEditSlots} sx={{ color: '#dc2626' }}>
                                        <Clear fontSize="small" />
                                      </IconButton>
                                    </>
                                  ) : (
                                    <>
                                      <Chip
                                        label={`${avail} available`}
                                        size="small"
                                        color={avail === 0 ? 'error' : avail <= 2 ? 'warning' : 'success'}
                                        sx={{ fontWeight: 700, fontSize: 11 }}
                                      />
                                      <IconButton
                                        size="small"
                                        onClick={() => startEditSlots(s)}
                                        sx={{ color: '#1565c0' }}
                                      >
                                        <Edit fontSize="small" />
                                      </IconButton>
                                    </>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>

                            {/* Charging Now */}
                            <TableCell align="center">
                              <Box sx={{ minWidth: 140 }}>
                                {s.active_charging_sessions > 0 ? (
                                  <Box>
                                    <Chip
                                      label={`${s.active_charging_sessions} vehicle${s.active_charging_sessions > 1 ? 's' : ''}`}
                                      size="small"
                                      color="success"
                                      icon={<BatteryChargingFull />}
                                      sx={{ fontWeight: 700, fontSize: 11, mb: 0.5 }}
                                    />
                                    {s.charging_vehicles && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        sx={{
                                          fontSize: 10,
                                          maxWidth: 140,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap'
                                        }}
                                      >
                                        {s.charging_vehicles}
                                      </Typography>
                                    )}
                                  </Box>
                                ) : (
                                  <Chip
                                    label="None"
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontWeight: 600, fontSize: 11, color: '#94a3b8' }}
                                  />
                                )}
                              </Box>
                            </TableCell>

                            {/* Status */}
                            <TableCell align="center">
                              <Switch
                                size="small"
                                checked={!!s.is_active}
                                onChange={() => handleToggleActive(s)}
                                sx={{
                                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#16a34a' },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#16a34a' },
                                }}
                              />
                              <Typography variant="caption" display="block" sx={{ fontSize: 10, color: s.is_active ? '#16a34a' : '#64748b', fontWeight: 600 }}>
                                {s.is_active ? 'Active' : 'Inactive'}
                              </Typography>
                            </TableCell>

                            {/* Actions */}
                            <TableCell align="center">
                              <Tooltip title="Edit Station">
                                <IconButton
                                  size="small"
                                  onClick={() => openEdit(s)}
                                  sx={{
                                    color: '#1565c0',
                                    '&:hover': { bgcolor: '#e3f2fd' }
                                  }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={e => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
                sx={{ borderTop: '1px solid #e2e8f0', color: '#1a237e' }}
              />
            </>
          )}
        </Card>
      )}

      {/* ── Map View ────────────────────────────────────────────────────────── */}
      {tab === 1 && (
        <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          {/* Map legend */}
          <Box sx={{ px: 2.5, py: 1.5, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
            <Typography variant="body2" fontWeight={700} color="#1a237e">Map Legend:</Typography>
            {[
              { label: 'Available', color: '#16a34a' },
              { label: 'Limited (≤60%)', color: '#d97706' },
              { label: 'Full / Inactive', color: '#dc2626' },
            ].map(l => (
              <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: l.color }} />
                <Typography variant="caption" color="text.secondary">{l.label}</Typography>
              </Box>
            ))}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {validStations.length} station{validStations.length !== 1 ? 's' : ''} with GPS
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#1a237e' }} />
            </Box>
          ) : validStations.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 10, color: '#94a3b8' }}>
              <LocationOn sx={{ fontSize: 56, mb: 1.5, opacity: 0.3 }} />
              <Typography variant="h6" fontWeight={600} color="#64748b">No GPS Coordinates</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Add latitude and longitude to stations to see them on the map.
              </Typography>
            </Box>
          ) : (
            <MapContainer
              center={[parseFloat(validStations[0].latitude), parseFloat(validStations[0].longitude)]}
              zoom={12}
              style={{ height: 540 }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {validStations.map((s) => {
                const avail = parseInt(s.available_slots) || 0;
                const total = parseInt(s.total_slots) || 0;
                return (
                  <Marker
                    key={s.id}
                    position={[parseFloat(s.latitude), parseFloat(s.longitude)]}
                    icon={mapIcon(avail, total, s.is_active)}
                  >
                    <Popup>
                      <Box sx={{ minWidth: 200 }}>
                        <Typography variant="subtitle2" fontWeight={700}>{s.name}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          {s.address}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                          <span style={{ fontSize: 12 }}>Available slots:</span>
                          <strong style={{ color: avail === 0 ? '#dc2626' : '#16a34a' }}>
                            {avail} / {total}
                          </strong>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 12 }}>Currently charging:</span>
                          <strong style={{ color: s.active_charging_sessions > 0 ? '#059669' : '#64748b' }}>
                            {s.active_charging_sessions} vehicles
                          </strong>
                        </Box>
                        {s.charging_vehicles && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Vehicles: {s.charging_vehicles}
                            </Typography>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 12 }}>Status:</span>
                          <strong style={{ color: s.is_active ? '#16a34a' : '#dc2626' }}>
                            {s.is_active ? 'Active' : 'Inactive'}
                          </strong>
                        </Box>
                      </Box>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          )}
        </Card>
      )}

      {/* ── Battery Monitoring View ──────────────────────────────────────────── */}
      {tab === 2 && (
        <Box>
          {/* Battery Stats Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={2.4}>
              <Card sx={{
                borderRadius: 2,
                bgcolor: '#fef2f2',
                border: '1px solid #fecaca'
              }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="caption" color="#DC2626" fontWeight={700}>
                    Critical (&lt;20%)
                  </Typography>
                  <Typography variant="h4" fontWeight={800} color="#dc2626">
                    {batteryData.stats?.critical || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* <Grid item xs={6} sm={6} md={2.4}>
              <Card sx={{
                borderRadius: 2,
                background: 'linear-gradient(135deg, #DC2626, #EF4444)',
                color: 'white',
                boxShadow: '0 4px 12px rgba(220,38,38,0.3)',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(220,38,38,0.4)' }
              }}>
                <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
                  <BatteryAlert sx={{ fontSize: 44, mb: 1, opacity: 0.9 }} />
                  <Typography variant="h3" fontWeight={800} sx={{ mb: 0.5 }}>
                    {batteryData.stats?.critical || 0}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ opacity: 0.95 }}>
                    Critical (&lt;20%)
                  </Typography>
                </CardContent>
              </Card>
            </Grid> */}
            <Grid item xs={2.4}>
              <Card sx={{
                borderRadius: 2,
                bgcolor: '#fae5ccff',
                border: '1px solid #f5be7fff'
              }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="caption" color="#D97706" fontWeight={700}>
                     Low (20-50%)
                  </Typography>
                  <Typography variant="h4" fontWeight={800} color="#D97706">
                    {batteryData.stats?.low || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {/* <Grid item xs={6} sm={6} md={2.4}>
              <Card sx={{
                borderRadius: 2,
                background: 'linear-gradient(135deg, #D97706, #F59E0B)',
                color: 'white',
                boxShadow: '0 4px 12px rgba(217,119,6,0.3)',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(217,119,6,0.4)' }
              }}>
                <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
                  <Battery50 sx={{ fontSize: 44, mb: 1, opacity: 0.9 }} />
                  <Typography variant="h3" fontWeight={800} sx={{ mb: 0.5 }}>
                    {batteryData.stats?.low || 0}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ opacity: 0.95 }}>
                    Low (20-50%)
                  </Typography>
                </CardContent>
              </Card>
            </Grid> */}
            <Grid item xs={2.4}>
              <Card sx={{
                borderRadius: 2,
                bgcolor: '#c7ecf6ff',
                border: '1px solid #90e0f5ff'
              }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="caption" color="#0891B2" fontWeight={700}>
                     Medium (50-80%)
                  </Typography>
                  <Typography variant="h4" fontWeight={800} color="#0891B2">
                    {batteryData.stats?.medium || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {/* <Grid item xs={6} sm={6} md={2.4}>
              <Card sx={{
                borderRadius: 2,
                background: 'linear-gradient(135deg, #0891B2, #06B6D4)',
                color: 'white',
                boxShadow: '0 4px 12px rgba(8,145,178,0.3)',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(8,145,178,0.4)' }
              }}>
                <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
                  <Battery80 sx={{ fontSize: 44, mb: 1, opacity: 0.9 }} />
                  <Typography variant="h3" fontWeight={800} sx={{ mb: 0.5 }}>
                    {batteryData.stats?.medium || 0}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ opacity: 0.95 }}>
                    Medium (50-80%)
                  </Typography>
                </CardContent>
              </Card>
            </Grid> */}
            <Grid item xs={2.4}>
              <Card sx={{
                borderRadius: 2,
                bgcolor: '#c2f4c5ff',
                border: '1px solid #26dc2fff'
              }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="caption" color="rgb(9, 116, 14)" fontWeight={700}>
                     Good (&gt;80%)
                  </Typography>
                  <Typography variant="h4" fontWeight={800} color="rgb(9, 116, 14)">
                    {batteryData.stats?.good || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {/* <Grid item xs={6} sm={6} md={2.4}>
              <Card sx={{
                borderRadius: 2,
                background: 'linear-gradient(135deg, #16A34A, #22C55E)',
                color: 'white',
                boxShadow: '0 4px 12px rgba(22,163,74,0.3)',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(22,163,74,0.4)' }
              }}>
                <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
                  <BatteryFull sx={{ fontSize: 44, mb: 1, opacity: 0.9 }} />
                  <Typography variant="h3" fontWeight={800} sx={{ mb: 0.5 }}>
                    {batteryData.stats?.good || 0}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ opacity: 0.95 }}>
                    Good (&gt;80%)
                  </Typography>
                </CardContent>
              </Card>
            </Grid> */}
            <Grid item xs={2.4}>
              <Card sx={{
                borderRadius: 2,
                bgcolor: 'rgb(139, 248, 145)',
                border: '1px solid #26dc2fff'
              }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="caption" color="rgb(9, 116, 14)" fontWeight={700}>
                     Charging Now
                  </Typography>
                  <Typography variant="h4" fontWeight={800} color="rgb(9, 116, 14)">
                    {batteryData.stats?.charging || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {/* <Grid item xs={6} sm={6} md={2.4}>
              <Card sx={{
                borderRadius: 2,
                background: 'linear-gradient(135deg, #059669, #10B981)',
                color: 'white',
                boxShadow: '0 4px 12px rgba(5,150,105,0.3)',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(5,150,105,0.4)' }
              }}>
                <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
                  <BatteryChargingFull sx={{ fontSize: 44, mb: 1, opacity: 0.9 }} />
                  <Typography variant="h3" fontWeight={800} sx={{ mb: 0.5 }}>
                    {batteryData.stats?.charging || 0}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ opacity: 0.95 }}>
                    Charging Now
                  </Typography>
                </CardContent>
              </Card>
            </Grid> */}
          </Grid>

          {/* Vehicle Battery Status */}
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', mb: 3 }}>
            <Box sx={{
              p: 2.5,
              borderBottom: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg,#1a237e,#1565c0)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(26,35,126,0.3)'
                  }}>
                    <ElectricCar sx={{ color: '#fff', fontSize: 26 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={800} color="#1a237e">
                      Vehicle Battery Status
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                      Real-time monitoring of all electric vehicles
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, bgcolor: '#f1f5f9', borderRadius: 2 }}>
                    <Switch
                      size="small"
                      checked={autoRefresh}
                      onChange={(e) => setAutoRefresh(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#16a34a' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#16a34a' },
                      }}
                    />
                    <Typography variant="caption" fontWeight={600} color="#64748b">
                      Auto Refresh
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Refresh />}
                    onClick={loadBatteryData}
                    sx={{
                      borderRadius: 2,
                      borderColor: '#cbd5e1',
                      color: '#475569',
                      fontWeight: 600,
                      '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' }
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Warning />}
                    onClick={checkBatteryAlerts}
                    sx={{
                      borderRadius: 2,
                      bgcolor: '#DC2626',
                      fontWeight: 600,
                      boxShadow: '0 2px 8px rgba(220,38,38,0.3)',
                      '&:hover': { bgcolor: '#B91C1C', boxShadow: '0 4px 12px rgba(220,38,38,0.4)' }
                    }}
                  >
                    Check Alerts
                  </Button>
                </Box>
              </Box>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>Vehicle</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>Model</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>Battery Level</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>Capacity</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>Assigned To</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12 }}>Last Update</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12, textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(batteryData.vehicles || []).map((vehicle) => (
                    <TableRow key={vehicle.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getBatteryIcon(vehicle.battery_pct, vehicle.charging_status === 'charging')}
                          <Typography fontWeight={700} fontSize={13} color="#1a237e">
                            {vehicle.vehicle_number}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={12} color="text.secondary">
                          {vehicle.model}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ minWidth: 140 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'center' }}>
                            <Typography variant="body2" fontWeight={700}>
                              {vehicle.battery_pct}%
                            </Typography>
                            {vehicle.charging_status === 'charging' && (
                              <Chip
                                label="Charging"
                                size="small"
                                color="success"
                                sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
                              />
                            )}
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={vehicle.battery_pct}
                            sx={{
                              height: 8,
                              borderRadius: 2,
                              bgcolor: '#e5e7eb',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 2,
                                bgcolor: getBatteryColor(vehicle.battery_pct)
                              }
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${vehicle.battery_capacity} kWh`}
                          size="small"
                          sx={{
                            bgcolor: '#e0f2fe',
                            color: '#0369a1',
                            fontWeight: 700,
                            fontSize: 11
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={vehicle.status_text}
                          color={
                            vehicle.battery_pct < 20 ? 'error' :
                              vehicle.battery_pct < 50 ? 'warning' : 'success'
                          }
                          size="small"
                          sx={{ fontWeight: 700, fontSize: 11 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={12} color="text.secondary">
                          {vehicle.assigned_user || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={11} color="text.secondary">
                          {vehicle.last_update ?
                            new Date(vehicle.last_update).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : '—'
                          }
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="View Analytics">
                            <IconButton
                              size="small"
                              onClick={() => loadAnalytics(vehicle.id)}
                              sx={{ color: '#1565c0', '&:hover': { bgcolor: '#e3f2fd' } }}
                            >
                              <Analytics fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Charging History">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedVehicle(vehicle);
                                setChargingDialog(true);
                              }}
                              sx={{ color: '#059669', '&:hover': { bgcolor: '#d1fae5' } }}
                            >
                              <EvStation fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(batteryData.vehicles || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <ElectricCar sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                        <Typography color="text.secondary" fontWeight={600}>
                          No vehicle data available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          <Grid container spacing={3}>
            {/* Battery Alerts */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', height: '100%' }}>
                <Box sx={{
                  p: 2,
                  borderBottom: '1px solid #e2e8f0',
                  background: 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #DC2626, #EF4444)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(220,38,38,0.3)'
                    }}>
                      <Notifications sx={{ color: '#fff', fontSize: 22 }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={800} color="#1a237e" fontSize={15}>
                        Battery Alerts
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Recent critical notifications
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 11, bgcolor: '#f8fafc' }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 11, bgcolor: '#f8fafc' }}>Vehicle</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 11, bgcolor: '#f8fafc' }}>Battery</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 11, bgcolor: '#f8fafc' }}>Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {alerts.slice(0, 10).map((alert) => (
                        <TableRow key={alert.id} hover>
                          <TableCell>
                            <Chip
                              label={alert.alert_type?.replace('_', ' ').toUpperCase()}
                              color={alert.alert_type === 'critical_battery' ? 'error' : 'warning'}
                              size="small"
                              sx={{ fontSize: 9, height: 20, fontWeight: 700 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700} fontSize={11}>
                              {alert.vehicle_number}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" fontSize={9}>
                              {alert.model}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              fontWeight={800}
                              fontSize={12}
                              color={getBatteryColor(alert.battery_level)}
                            >
                              {alert.battery_level}%
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography fontSize={10} color="text.secondary">
                              {new Date(alert.created_at).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                      {alerts.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                            <CheckCircle sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                            <Typography color="text.secondary" fontSize={12} fontWeight={600}>
                              No alerts found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>

            {/* Active Charging Sessions */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', height: '100%' }}>
                <Box sx={{
                  p: 2,
                  borderBottom: '1px solid #e2e8f0',
                  background: 'linear-gradient(135deg, #ecfdf5 0%, #ffffff 100%)'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #059669, #10B981)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(5,150,105,0.3)'
                    }}>
                      <BatteryChargingFull sx={{ color: '#fff', fontSize: 22 }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={800} color="#1a237e" fontSize={15}>
                        Active Charging
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Currently charging vehicles
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 11, bgcolor: '#f8fafc' }}>Vehicle</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 11, bgcolor: '#f8fafc' }}>Station</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 11, bgcolor: '#f8fafc' }}>Duration</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 11, bgcolor: '#f8fafc' }}>Battery</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 11, bgcolor: '#f8fafc' }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {chargingSessions.filter(session => session.session_status === 'active').slice(0, 10).map((session) => {
                        const startTime = new Date(session.start_time);
                        const duration = Math.round((new Date() - startTime) / 60000);
                        return (
                          <TableRow key={session.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={700} fontSize={11}>
                                {session.vehicle_number}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" fontSize={9}>
                                {session.model}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography fontSize={11} color="text.secondary">
                                {session.station_name || 'Auto-detected'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={`${duration} min`}
                                size="small"
                                color={duration > 60 ? 'warning' : 'success'}
                                sx={{ fontSize: 9, height: 20, fontWeight: 700 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography
                                fontWeight={800}
                                fontSize={12}
                                color={getBatteryColor(session.start_battery_pct)}
                              >
                                {session.start_battery_pct}%
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label="CHARGING"
                                size="small"
                                color="success"
                                icon={<BatteryChargingFull />}
                                sx={{ fontSize: 9, height: 20, fontWeight: 700 }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {chargingSessions.filter(session => session.session_status === 'active').length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <BatteryChargingFull sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                            <Typography color="text.secondary" fontSize={12} fontWeight={600}>
                              No active charging sessions
                            </Typography>
                            <Typography variant="caption" color="text.secondary" fontSize={10}>
                              Sessions start/end automatically from tracker
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* ── Add / Edit Dialog ───────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e', display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          {editStation ? 'Edit Station' : 'Add Battery Station'}
          <IconButton size="small" onClick={() => setDialogOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Grid container spacing={2.5}>

            {/* Identity */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.8 }}>
                Station Info
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Station Name *"
                value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Address *" multiline rows={2}
                value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
            </Grid>

            {/* GPS */}
            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.8 }}>
                GPS Coordinates
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Latitude" type="number"
                value={form.latitude} onChange={e => setForm({ ...form, latitude: e.target.value })}
                helperText="e.g. 28.6139" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Longitude" type="number"
                value={form.longitude} onChange={e => setForm({ ...form, longitude: e.target.value })}
                helperText="e.g. 77.2090" />
            </Grid>

            {/* Slots */}
            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.8 }}>
                Slot Configuration
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Total Slots *" type="number"
                value={form.total_slots}
                onChange={e => {
                  const val = e.target.value;
                  setForm(f => ({
                    ...f,
                    total_slots: val,
                    // Auto-fill available if not editing
                    available_slots: editStation ? f.available_slots : val,
                  }));
                }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Available Slots" type="number"
                value={form.available_slots}
                onChange={e => setForm({ ...form, available_slots: e.target.value })}
                helperText="Leave blank to match total" />
            </Grid>

            {/* Charging Specifications */}
            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.8 }}>
                Charging Specifications
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Charging Rate (kW)" type="number"
                value={form.charging_rate_kw}
                onChange={e => setForm({ ...form, charging_rate_kw: e.target.value })}
                helperText="Power output" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Price per kWh (₹)" type="number"
                value={form.pricing_per_kwh}
                onChange={e => setForm({ ...form, pricing_per_kwh: e.target.value })}
                helperText="Leave blank if free" />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Connector Types</InputLabel>
                <Select
                  multiple
                  value={form.connector_types || []}
                  onChange={e => setForm({ ...form, connector_types: e.target.value })}
                  label="Connector Types"
                  renderValue={(selected) => selected.join(', ').toUpperCase()}
                >
                  <MenuItem value="type1">Type 1 (J1772)</MenuItem>
                  <MenuItem value="type2">Type 2 (Mennekes)</MenuItem>
                  <MenuItem value="ccs">CCS (Combined Charging)</MenuItem>
                  <MenuItem value="chademo">CHAdeMO</MenuItem>
                  <MenuItem value="tesla">Tesla Proprietary</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Availability Status</InputLabel>
                <Select
                  value={form.availability_status || 'available'}
                  onChange={e => setForm({ ...form, availability_status: e.target.value })}
                  label="Availability Status"
                >
                  <MenuItem value="available">Available</MenuItem>
                  <MenuItem value="occupied">Occupied</MenuItem>
                  <MenuItem value="maintenance">Under Maintenance</MenuItem>
                  <MenuItem value="offline">Offline</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Active toggle */}
            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.is_active}
                    onChange={e => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#1a237e' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#1a237e' },
                    }}
                  />
                }
                label={<Typography variant="body2" fontWeight={600}>{form.is_active ? 'Station Active' : 'Station Inactive'}</Typography>}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#64748b' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, px: 4, fontWeight: 700 }}
          >
            {saving
              ? <CircularProgress size={18} sx={{ color: '#fff' }} />
              : editStation ? 'Update Station' : 'Add Station'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ─────────────────────────────────────────────────────────── */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ fontWeight: 600 }}>
          {snack.msg}
        </Alert>
      </Snackbar>

      {/* ── Analytics Dialog ─────────────────────────────────────────────────── */}
      <Dialog
        open={analyticsDialog}
        onClose={() => setAnalyticsDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{
          fontWeight: 800,
          color: '#1a237e',
          background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pb: 2
        }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            background: 'linear-gradient(135deg,#1a237e,#1565c0)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(26,35,126,0.3)'
          }}>
            <Timeline sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              lineHeight: 1.2,
            }}>
            <Typography variant="h6" fontWeight={800} fontSize={16}>
              Battery Analytics
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={500}>
              {analytics?.period || '7 days'} performance overview
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {analytics && (
            <Box>
              {/* Summary Cards */}
              {/* {analytics.daily_battery?.length > 0 && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={4}>
                    <Card sx={{
                      borderRadius: 2,
                      bgcolor: '#fef2f2',
                      border: '1px solid #fecaca'
                    }}>
                      <CardContent sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="caption" color="#dc2626" fontWeight={700}>
                          MIN LEVEL
                        </Typography>
                        <Typography variant="h4" fontWeight={800} color="#dc2626">
                          {Math.min(...analytics.daily_battery.map(d => d.min_battery))}%
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card sx={{
                      borderRadius: 2,
                      bgcolor: '#ecfdf5',
                      border: '1px solid #a7f3d0'
                    }}>
                      <CardContent sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="caption" color="#059669" fontWeight={700}>
                          MAX LEVEL
                        </Typography>
                        <Typography variant="h4" fontWeight={800} color="#059669">
                          {Math.max(...analytics.daily_battery.map(d => d.max_battery))}%
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card sx={{
                      borderRadius: 2,
                      bgcolor: '#eff6ff',
                      border: '1px solid #bfdbfe'
                    }}>
                      <CardContent sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="caption" color="#1565c0" fontWeight={700}>
                          AVG LEVEL
                        </Typography>
                        <Typography variant="h4" fontWeight={800} color="#1565c0">
                          {Math.round(analytics.daily_battery.reduce((sum, d) => sum + d.avg_battery, 0) / analytics.daily_battery.length)}%
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )} */}

              {/* Daily Battery Table */}
              {/* <Typography variant="subtitle1" fontWeight={700} color="#1a237e" sx={{ mb: 1.5 }}>
                Daily Battery Levels
              </Typography> */}
              {analytics.daily_battery?.length > 0 ? (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                        <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 12 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 12 }}>Min Level</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 12 }}>Max Level</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 12 }}>Avg Level</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 12 }}>Readings</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analytics.daily_battery.map((day, i) => (
                        <TableRow key={i} hover>
                          <TableCell>
                            <Typography fontSize={12} fontWeight={600}>
                              {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${Math.round(day.min_battery)}%`}
                              size="small"
                              sx={{
                                bgcolor: '#fef2f2',
                                color: '#dc2626',
                                fontWeight: 700,
                                fontSize: 11
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${Math.round(day.max_battery)}%`}
                              size="small"
                              sx={{
                                bgcolor: '#ecfdf5',
                                color: '#059669',
                                fontWeight: 700,
                                fontSize: 11
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${Math.round(day.avg_battery)}%`}
                              size="small"
                              sx={{
                                bgcolor: '#eff6ff',
                                color: '#1565c0',
                                fontWeight: 700,
                                fontSize: 11
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography fontSize={11} color="text.secondary">
                              {day.readings} records
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Analytics sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                  <Typography color="text.secondary" fontWeight={600}>
                    No battery data available for this period
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Button
            onClick={() => setAnalyticsDialog(false)}
            variant="contained"
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              background: 'linear-gradient(135deg,#1a237e,#1565c0)',
              px: 4
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Charging Sessions Dialog ────────────────────────────────────────── */}
      <Dialog
        open={chargingDialog}
        onClose={() => setChargingDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{
          fontWeight: 800,
          color: '#1a237e',
          background: 'linear-gradient(135deg, #ecfdf5 0%, #ffffff 100%)',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pb: 2
        }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #059669, #10B981)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(5,150,105,0.3)'
          }}>
            <EvStation sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              lineHeight: 1.2,
            }}>
            <Typography variant="h6" fontWeight={800} fontSize={16}>
              Charging History
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={500}>
              {selectedVehicle?.vehicle_number} - All charging sessions
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 12 }}>Station</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 12 }}>Start Time</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 12 }}>Duration</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 12 }}>Battery Change</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 12 }}>Energy Added</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 12 }}>Cost</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: 12, textAlign: 'center' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {chargingSessions
                  .filter(session => session.vehicle_id === selectedVehicle?.id)
                  .map((session) => {
                    const duration = session.end_time ?
                      Math.round((new Date(session.end_time) - new Date(session.start_time)) / 60000) :
                      Math.round((new Date() - new Date(session.start_time)) / 60000);
                    const batteryGain = (session.end_battery_pct || 0) - session.start_battery_pct;

                    return (
                      <TableRow key={session.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EvStation sx={{ fontSize: 18, color: '#059669' }} />
                            <Typography fontSize={12} fontWeight={600}>
                              {session.station_name || 'Auto-detected'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={11} color="text.secondary">
                            {new Date(session.start_time).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${duration} min`}
                            size="small"
                            color={duration > 60 ? 'warning' : 'success'}
                            sx={{ fontWeight: 700, fontSize: 10 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography fontSize={11} color="text.secondary">
                              {session.start_battery_pct}%
                            </Typography>
                            <Typography fontSize={11} color="text.secondary">→</Typography>
                            <Typography fontSize={11} fontWeight={700} color={session.end_battery_pct ? '#059669' : '#94a3b8'}>
                              {session.end_battery_pct || '—'}%
                            </Typography>
                            {batteryGain > 0 && (
                              <Chip
                                label={`+${batteryGain}%`}
                                size="small"
                                sx={{
                                  bgcolor: '#ecfdf5',
                                  color: '#059669',
                                  fontSize: 9,
                                  height: 18,
                                  fontWeight: 700
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={11} color="text.secondary">
                            {session.energy_added_kwh || '—'} kWh
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={11} fontWeight={600} color="#1565c0">
                            {session.cost_amount ? `$${session.cost_amount}` : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={session.session_status?.toUpperCase()}
                            color={session.session_status === 'completed' ? 'success' : 'primary'}
                            size="small"
                            sx={{ fontWeight: 700, fontSize: 10 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {chargingSessions.filter(session => session.vehicle_id === selectedVehicle?.id).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <BatteryChargingFull sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                      <Typography color="text.secondary" fontWeight={600}>
                        No charging sessions found for this vehicle
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontSize={10}>
                        Charging sessions are automatically detected by the tracker
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Button
            onClick={() => setChargingDialog(false)}
            variant="contained"
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #059669, #10B981)',
              px: 4
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
